import { captureException } from "@sentry/browser";
import { Immutable } from "immer";
import React, { type JSX, useEffect, useRef, useState } from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import * as userAPI from "../../../api/user";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { ErrorModal } from "../../../components/ErrorModal";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { config } from "../../../config";
import { CaptchaLibrary } from "../../../forms/Captcha";
import { FORM_ERROR_SELECTOR } from "../../../forms/classNames";
import { strings } from "../../../localization";
import { IBasket } from "../../../models/catalogue";
import { IProductSKU, isoWebPageURL } from "../../../models/nominals";
import { setUser } from "../../../store/common/reducers";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import * as analytics from "../../../utils/analytics";
import { trackFormEvent } from "../../../utils/analytics";
import { papply } from "../../../utils/functional";
import { focusWithTemporaryTabIndex } from "../../../utils/keyboardFocus";
import { guardUnhandledAction } from "../../../utils/never";
import { navigateToHome, navigateToURL } from "../../../utils/urls";
import { BasketSummary } from "../BasketSummary";
import { BasketVoucherForm } from "../BasketVoucherForm";
import { BillingAddress, IPreScreenModalProps } from "../BillingAddress";
import { CSRHeader } from "../CSRHeader";
import { EmailAddress } from "../EmailAddress";
import { PaymentChildFrameListener } from "../PaymentChildFrameListener";
import { PaymentMethods } from "../PaymentMethods";
import { ShippingAddress } from "../ShippingAddress";
import { ShippingMethod } from "../ShippingMethod";
import {
    PRIMARY_PAYMENT_METHOD_KEY,
    PredictedDeliveryDateDisplayType,
    PreferredDeliveryDateDisplayType,
    SECONDARY_PAYMENT_METHOD_KEY,
    Step,
    StepIndexCheckoutFormNameMap,
} from "../constants";
import { defaults } from "../defaults";
import { resetPaymentStates, setFields, updatePaymentError } from "../reducers";
import {
    addVoucherCode,
    loadAssistedUser,
    loadBasket,
    loadCountries,
    loadEmailAddress,
    loadPaymentMethods,
    loadShippingAndBillingAddresses,
    loadUserAddresses,
    placeOrder,
    removeVoucherCode,
    saveBillingAddress,
    saveEmailAddress,
    savePreferredDeliveryDate,
    saveShippingAddress,
    saveShippingMethod,
} from "../thunks";
import { ICheckoutErrorBody, ICheckoutHTTPError } from "../types";
import { IErrorMsgTemplateContext, renderErrorMessageTemplate } from "../utils";

import styles from "./index.module.scss";

type IStepEventHandler = (event?: React.MouseEvent<HTMLElement>) => void;

const stepNames: { [s in Step]: string } = {
    [Step.LOGIN]: "login",
    [Step.SHIPPING_ADDRESS]: "shipping-address",
    [Step.SHIPPING_METHOD]: "shipping-method",
    [Step.BILLING_ADDRESS]: "billing-method",
    [Step.PAYMENT_METHODS]: "payment-method",
    [Step.PLACING_ORDER]: "placing-order",
};

const isStep = (s: Step | string): s is Step => {
    return (s as string).substr === undefined;
};

interface IStepErrors {
    global?: string[];
    basket?: string[];
    shipping_address?: string[];
    shipping_method?: string[];
    payment_method?: string[];
    billing_address?: string[];
}

const TABLET_WIDTH_THRESHOLD = 769;

const isTabletOrSmallerWidth = () => {
    const window = getWindow();
    if (!window) {
        return false;
    }
    return window.innerWidth < TABLET_WIDTH_THRESHOLD;
};

const trackPageView = (basket: IBasket) => {
    const skus = basket.lines.reduce<IProductSKU[]>(
        (skuList, line) => skuList.concat(line.product.skus),
        [],
    );
    const price = basket.total_excl_tax;
    analytics.trackProductValuePageView(skus, price, "cart", "CartPageView");
    analytics.trackBasketEvent("begin_checkout", basket);
};

const scrollToFirstErrorMessage = () => {
    const document = getDocument();
    if (!document) {
        return;
    }

    const setScrollTop = (px: number) => {
        document.body.scrollTop = px;
        if (document.documentElement) {
            document.documentElement.scrollTop = px;
        }
    };
    const error = document.querySelector(FORM_ERROR_SELECTOR);
    if (!error) {
        setScrollTop(0);
        return;
    }
    error.scrollIntoView(true);
    setScrollTop(Math.max(0, document.body.scrollTop - 80));
};

const sendAnalyticsData = (
    step: Step | string,
    formRef: React.RefObject<HTMLFormElement>,
    basket: IBasket | null,
    currentStep: Step,
) => {
    const stepName = isStep(step) ? stepNames[step] : step;
    analytics.trackVirtualSubPage(stepName);
    if (formRef.current) {
        trackFormEvent(
            "form_submit",
            StepIndexCheckoutFormNameMap[currentStep],
            formRef.current,
            basket,
        );
    }
};

const focusStep = (step: Step) => {
    let stepName = isStep(step) ? stepNames[step] : step;
    if (stepName === "billing-method") {
        stepName = "billing-address";
    }
    if (stepName === "login") {
        stepName = "email";
    }
    const document = getDocument();
    if (!document) {
        return;
    }
    const stepElem = document.querySelector<HTMLElement>(
        `.checkout-step--${stepName}`,
    );
    if (!stepElem) {
        return;
    }
    const heading = stepElem.querySelector<HTMLElement>(
        ".checkout-step__heading",
    );
    const focusTarget = heading ?? stepElem;
    // No `focusVisible: true`: the heading is not interactive, so forcing the
    // ring paints an outline around a plain <h2> for mouse users too. The
    // browser's own heuristic carries :focus-visible over from a
    // keyboard-activated Continue button / Edit link to this scripted focus
    // move -- including across the re-render that unmounts that control -- so
    // keyboard users still get the ring without forcing it on everyone.
    focusWithTemporaryTabIndex(focusTarget);
};

export const Checkout = (
    props: Immutable<{
        formSteps?: Step[];
        getExtraSummaryContent: () => JSX.Element | null;
        getExtraSidebarContent: () => JSX.Element | null;
        buildFinancingUpsell: (grandTotal: string) => JSX.Element | null;
        buildFortivaFinancingUpsell?: (
            grandTotal: string,
        ) => JSX.Element | null;
        showFinancingPreQual?: boolean;
        buildPreScreenModal?: (
            props: IPreScreenModalProps,
        ) => JSX.Element | null;
        enableSplitPay?: boolean;
        disableFreeShippingMessage?: boolean;
        predictedDeliveryDates?: PredictedDeliveryDateDisplayType;
        preferredDeliveryDates?: PreferredDeliveryDateDisplayType;
        preferredDeliveryContent?: React.ReactNode;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.common.user);
    const checkoutState = useAppSelector((state) => state.checkout || defaults);

    const containerRef = useRef<HTMLDivElement>(null);
    const basketRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isStickyActive, setIsStickyActive] = useState(true);
    const [messages, setMessages] = useState<IStepErrors>({});

    const handleSaveEmailAddress = () => {
        return dispatch(saveEmailAddress()).unwrap();
    };

    const handleSaveShippingAddress = async (
        bypassAddressValidation: boolean,
    ) => {
        const result = await dispatch(
            saveShippingAddress({ bypassAddressValidation }),
        ).unwrap();
        if (formRef.current) {
            analytics.trackStep1ShippingCompleteEvent(
                formRef.current,
                checkoutState.data.basket,
            );
        }
        return result;
    };

    const handleSaveBillingAddress = async (
        bypassAddressValidation: boolean,
    ) => {
        const result = await dispatch(
            saveBillingAddress({ bypassAddressValidation }),
        ).unwrap();
        if (formRef.current) {
            analytics.trackStep3BillingCompleteEvent(
                formRef.current,
                checkoutState.data.basket,
            );
        }
        return result;
    };

    const handleSaveShippingMethod = async () => {
        const result = await dispatch(saveShippingMethod()).unwrap();
        if (formRef.current) {
            analytics.trackStep2DeliveryCompleteEvent(
                checkoutState.form.shipping_method || "",
                formRef.current,
                result,
            );
        }
        return result;
    };

    const handleSavePreferredDeliveryDate = async (
        preferredDate: Date | null,
    ): Promise<void> => {
        await dispatch(
            savePreferredDeliveryDate({ date: preferredDate }),
        ).unwrap();
    };

    const checkStickyState = () => {
        setIsStickyActive(!isTabletOrSmallerWidth());
    };

    const gotoStep = (nextStep: Step) => {
        if (formRef.current) {
            sendAnalyticsData(
                nextStep,
                formRef as React.RefObject<HTMLFormElement>,
                checkoutState.data.basket,
                checkoutState.form.current_step,
            );
        }
        dispatch(
            setFields({
                current_step: nextStep,
            }),
        );
        requestAnimationFrame(() => {
            focusStep(nextStep);
        });
    };

    const onCheckoutError = (err: ICheckoutHTTPError) => {
        console.error(err);
        // `err.response.body` is a structured ICheckoutErrorBody only when the server returned
        // a JSON validation error (bad address, declined payment, etc.) and no order was created.
        // For opaque failures like WAF 403s, gateway 5xx, timeouts, or any non-JSON body,
        // it may be a string or undefined. So normalize them to an empty object.
        const body = err && err.response ? err.response.body : undefined;
        const errors: ICheckoutErrorBody =
            body && typeof body === "object" ? body : {};
        let step: Step = Step.SHIPPING_ADDRESS;
        const newMessages: IStepErrors = {};
        const ctx: IErrorMsgTemplateContext = {
            basketID: checkoutState.data.basket
                ? checkoutState.data.basket.encoded_basket_id
                : undefined,
            orderID: checkoutState.data.order
                ? checkoutState.data.order.number
                : undefined,
        };
        if (errors.global) {
            step = Step.PAYMENT_METHODS;
            dispatch(updatePaymentError(errors.global[0]));
        } else if (errors.basket) {
            step = Step.SHIPPING_ADDRESS;
            newMessages.basket = errors.basket;
        } else if (errors.shipping_address) {
            step = Step.SHIPPING_ADDRESS;
            newMessages.shipping_address = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_SHIPPING_ADDRESS") || "",
                    ctx,
                ),
            ].concat(errors.shipping_address);
        } else if (errors.shipping_method_code) {
            step = Step.SHIPPING_METHOD;
            newMessages.shipping_method = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_SHIPPING_METHOD") || "",
                    ctx,
                ),
            ].concat(errors.shipping_method_code);
        } else if (errors.payment) {
            step = Step.PAYMENT_METHODS;
            newMessages.payment_method = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_PAYMENT_METHOD") || "",
                    ctx,
                ),
            ].concat(errors.payment);
        } else if (errors.billing_address) {
            step = Step.BILLING_ADDRESS;
            newMessages.billing_address = [
                renderErrorMessageTemplate(
                    strings.get("CHECKOUT_ERROR_BILLING_ADDRESS") || "",
                    ctx,
                ),
            ].concat(errors.billing_address);
        } else {
            // Surface the opaque/unexpected failure in Sentry.
            captureException(err);
            step = Step.SHIPPING_ADDRESS;
            newMessages.shipping_address = [
                renderErrorMessageTemplate(
                    strings.get(
                        ctx.orderID
                            ? "CHECKOUT_ERROR_ORDER_RECEIVED"
                            : "CHECKOUT_ERROR_FALLBACK",
                    ) || "",
                    ctx,
                ),
            ];
        }

        // Go to the first step with an error. Then, on the next tick, show the error message, and scroll the page up to view it.
        scrollToFirstErrorMessage();
        gotoStep(step);
        setTimeout(() => {
            setMessages(newMessages);
            setTimeout(() => {
                scrollToFirstErrorMessage();
            }, 0);
        }, 0);
    };

    const renderStepLogin = (
        onContinue: IStepEventHandler,
        onEdit: IStepEventHandler,
    ) => {
        // Only display the email step if the user already has an email address. Otherwise,
        // it becomes part of the shipping address step.
        let emailStep: JSX.Element | null = null;
        if (user && user.email && !user.is_csr) {
            emailStep = (
                <EmailAddress
                    key={Step.LOGIN}
                    user={user}
                    onContinue={onContinue}
                    onEdit={onEdit}
                />
            );
        }
        return emailStep;
    };

    const renderStepShippingAddress = (
        idx: number,
        onContinue: IStepEventHandler,
        onEdit: IStepEventHandler,
    ) => {
        const isCurrentStep =
            checkoutState.form.current_step === Step.SHIPPING_ADDRESS;
        return (
            <ShippingAddress
                key={Step.SHIPPING_ADDRESS}
                heading={`${idx}. ${strings.get(
                    "CHECKOUT_SECTION_TITLE_SHIPPING_ADDRESS",
                )}`}
                errors={messages.shipping_address || []}
                saveEmailAddress={handleSaveEmailAddress}
                saveShippingAddress={handleSaveShippingAddress}
                onContinue={onContinue}
                onEdit={onEdit}
                isCurrentStep={isCurrentStep}
                formRef={isCurrentStep ? formRef : undefined}
            />
        );
    };

    const renderStepShippingMethod = (
        idx: number,
        onContinue: IStepEventHandler,
        onEdit: IStepEventHandler,
    ) => {
        const isCurrentStep =
            checkoutState.form.current_step === Step.SHIPPING_METHOD;
        return (
            <ShippingMethod
                key={Step.SHIPPING_METHOD}
                heading={`${idx}. ${strings.get(
                    "CHECKOUT_SECTION_TITLE_SHIPPING_METHOD",
                )}`}
                errors={messages.shipping_method || []}
                predictedDeliveryDates={
                    props.predictedDeliveryDates ||
                    PredictedDeliveryDateDisplayType.DISABLED
                }
                preferredDeliveryDates={
                    props.preferredDeliveryDates ||
                    PreferredDeliveryDateDisplayType.DISABLED
                }
                preferredDeliveryContent={props.preferredDeliveryContent}
                saveShippingMethod={handleSaveShippingMethod}
                savePreferredDeliveryDate={handleSavePreferredDeliveryDate}
                onContinue={onContinue}
                onEdit={onEdit}
                isCurrentStep={isCurrentStep}
                formRef={isCurrentStep ? formRef : undefined}
            />
        );
    };

    const renderStepBillingAddress = (
        idx: number,
        onContinue: IStepEventHandler,
        onEdit: IStepEventHandler,
    ) => {
        const isCurrentStep =
            checkoutState.form.current_step === Step.BILLING_ADDRESS;
        return (
            <BillingAddress
                key={Step.BILLING_ADDRESS}
                heading={`${idx}. ${strings.get(
                    "CHECKOUT_SECTION_TITLE_BILLING_ADDRESS",
                )}`}
                errors={messages.billing_address || []}
                showFinancingPreQual={props.showFinancingPreQual}
                buildPreScreenModal={props.buildPreScreenModal}
                saveBillingAddress={handleSaveBillingAddress}
                onContinue={onContinue}
                onEdit={onEdit}
                isCurrentStep={isCurrentStep}
                formRef={isCurrentStep ? formRef : undefined}
            />
        );
    };

    const renderStepPaymentMethod = (
        idx: number,
        onContinue: IStepEventHandler,
    ) => {
        const submitOrder = async () => {
            dispatch(resetPaymentStates());
            onContinue();
            try {
                const primaryMethod =
                    checkoutState.form.payment_methods[
                        PRIMARY_PAYMENT_METHOD_KEY
                    ];
                const secondaryMethod =
                    checkoutState.form.payment_methods[
                        SECONDARY_PAYMENT_METHOD_KEY
                    ];
                const isSecondary =
                    secondaryMethod?.method_type &&
                    secondaryMethod.method_type !== "default";
                const paymentType = isSecondary
                    ? [primaryMethod?.method_type, secondaryMethod?.method_type]
                          .filter(Boolean)
                          .join(",")
                    : primaryMethod?.method_type;
                if (formRef.current) {
                    try {
                        analytics.trackStep4PaymentCompleteEvent(
                            paymentType || "",
                            formRef.current,
                            checkoutState.data.basket,
                        );
                    } catch {
                        // best-effort; basket state needed here, so analytics runs
                        // before placeOrder — errors must not prevent order placement
                    }
                }
                await dispatch(placeOrder({ user })).unwrap();
            } catch (err) {
                onCheckoutError(err as ICheckoutHTTPError);
            }
        };
        const isCSR = user && user.is_csr;
        const isCurrentStep =
            checkoutState.form.current_step === Step.PAYMENT_METHODS;
        return (
            <PaymentMethods
                key={Step.PAYMENT_METHODS}
                heading={`${idx}. ${strings.get(
                    "CHECKOUT_SECTION_TITLE_PAYMENT_METHODS",
                )}`}
                errors={messages.payment_method || []}
                buildFinancingUpsell={props.buildFinancingUpsell}
                buildFortivaFinancingUpsell={props.buildFortivaFinancingUpsell}
                enableSplitPay={props.enableSplitPay ? true : false}
                isCSR={!!isCSR}
                onContinue={submitOrder}
                isCurrentStep={isCurrentStep}
                formRef={isCurrentStep ? formRef : undefined}
            />
        );
    };

    const renderStep = (
        idx: number,
        step: Step,
        onContinue: IStepEventHandler,
        onEdit: IStepEventHandler,
    ) => {
        switch (step) {
            case Step.LOGIN:
                return renderStepLogin(onContinue, onEdit);
            case Step.SHIPPING_ADDRESS:
                return renderStepShippingAddress(idx, onContinue, onEdit);
            case Step.SHIPPING_METHOD:
                return renderStepShippingMethod(idx, onContinue, onEdit);
            case Step.BILLING_ADDRESS:
                return renderStepBillingAddress(idx, onContinue, onEdit);
            case Step.PAYMENT_METHODS:
                return renderStepPaymentMethod(idx, onContinue);
            case Step.PLACING_ORDER:
                return null;
            default:
                guardUnhandledAction(step);
        }
        return null;
    };

    const renderSteps = () => {
        if (!checkoutState.data.basket) {
            return null;
        }

        const handleGotoStep = (
            nextStep: Step,
            event: React.MouseEvent<HTMLElement> | undefined,
        ) => {
            if (event) {
                event.preventDefault();
            }
            gotoStep(nextStep);
        };

        // Determine what steps to show in the checkout process.
        const formSteps = props.formSteps || [
            Step.LOGIN,
            Step.SHIPPING_ADDRESS,
            Step.SHIPPING_METHOD,
            Step.BILLING_ADDRESS,
            Step.PAYMENT_METHODS,
            Step.PLACING_ORDER,
        ];
        const stepElems = formSteps.map((step, idx) => {
            const nextStep: Step | undefined = formSteps[idx + 1];
            const onContinue = papply(handleGotoStep, nextStep);
            const onEdit = papply(handleGotoStep, step);
            return renderStep(idx, step, onContinue, onEdit);
        });
        return (
            <div className="">
                <CSRHeader user={user} />
                {stepElems}
            </div>
        );
    };

    const handleRemoveVoucherCode = async (code: string) => {
        await dispatch(removeVoucherCode({ code })).unwrap();
    };

    const handleAddVoucherCode = async (code: string) => {
        await dispatch(addVoucherCode({ code })).unwrap();
    };

    const renderBasket = () => {
        return (
            <div className={styles.stickyContainer} ref={containerRef}>
                <div className="basket-summary" ref={basketRef}>
                    <h2 className="basket-summary__header">{t`Order Summary`}</h2>
                    <BasketSummary
                        data={checkoutState.data}
                        derived={checkoutState.derived}
                        removeVoucherCode={handleRemoveVoucherCode}
                        disableFreeShippingMessage={
                            props.disableFreeShippingMessage
                        }
                        getExtraContent={props.getExtraSummaryContent}
                    />
                    <div className="basket-summary__promocode">
                        <BasketVoucherForm
                            addVoucherCode={handleAddVoucherCode}
                            vouchers={checkoutState.data.basket?.vouchers || []}
                        />
                    </div>
                    <ErrorBoundary>
                        {props.getExtraSidebarContent()}
                    </ErrorBoundary>
                </div>
            </div>
        );
    };

    const renderBasketErrorModal = () => {
        const errorModalIsOpen = !!(
            messages.basket && messages.basket.length > 0
        );
        const redirectToBasket = (
            e: React.FormEvent<Element> | React.KeyboardEvent<Element>,
        ) => {
            e.preventDefault();
            navigateToURL(isoWebPageURL.wrap(reverse("basket:summary")));
        };
        return (
            <ErrorModal
                name="checkout"
                isOpen={errorModalIsOpen}
                onRequestClose={redirectToBasket}
                // TODO(i18n): Use t tagged template: closeButtonText={t`Edit Cart`}
                closeButtonText="Edit Cart"
            >
                {/* TODO(i18n): Use t tagged template: <p>{t`An error occurred with your order.`}</p> */}
                <p>An error occurred with your order.</p>
                <ul>
                    {(messages.basket || []).map((msg, i) => {
                        return <li key={i}>{msg}</li>;
                    })}
                </ul>
            </ErrorModal>
        );
    };

    // Effect: Force reload when page is restored from bfcache (Safari).
    // Safari aggressively caches pages in the back-forward cache, restoring
    // stale JS state when the user hits Back. On the checkout page this
    // causes errors because payment/basket state is no longer valid. A
    // reload makes Safari behave like Chrome, which already does a fresh
    // load — triggering the normal empty-basket → homepage redirect.
    useEffect(() => {
        const window = getWindow();
        if (!window) return;
        const handler = (event: PageTransitionEvent) => {
            if (event.persisted) {
                window.location.reload();
            }
        };
        window.addEventListener("pageshow", handler);
        return () => window.removeEventListener("pageshow", handler);
    }, []);

    // Effect: Component mount - async initialization
    useEffect(() => {
        const initialize = async () => {
            // Send loading event to GA
            if (formRef.current) {
                sendAnalyticsData(
                    "loading",
                    formRef as React.RefObject<HTMLFormElement>,
                    checkoutState.data.basket,
                    checkoutState.form.current_step,
                );
            }

            // Load all the data necessary to begin checkout
            const [basketResult, loadedUser] = await Promise.all([
                dispatch(loadBasket()),
                userAPI.load().then((u) => {
                    dispatch(setUser(u));
                    return u;
                }),
                dispatch(loadCountries()),
                dispatch(loadShippingAndBillingAddresses()),
                dispatch(loadPaymentMethods()),
            ]);

            const basket = loadBasket.fulfilled.match(basketResult)
                ? basketResult.payload.basket
                : null;

            // Redirect home if the user doesn't have any lines in their basket
            if (!basket || basket.lines.length <= 0) {
                navigateToHome();
                // TODO(i18n): Error message is developer-facing (logged exception), OK to leave untranslated
                throw new Error("Cannot checkout with empty basket");
            }

            // Load assisted user if the active user is a CSR
            if (loadedUser && loadedUser.is_csr) {
                try {
                    await dispatch(loadAssistedUser()).unwrap();
                } catch (e) {
                    console.warn(e);
                }
            }

            // Load the email address associated with the basket. Has to be deliberately
            // after the call to loadAssistedUser to prevent a race-condition.
            await Promise.all([
                dispatch(loadEmailAddress()),
                dispatch(loadUserAddresses()),
            ]);

            // Loading is complete
            if (formRef.current) {
                sendAnalyticsData(
                    checkoutState.form.current_step,
                    formRef as React.RefObject<HTMLFormElement>,
                    checkoutState.data.basket,
                    checkoutState.form.current_step,
                );
            }
            setIsLoading(false);

            trackPageView(basket);
        };
        initialize().catch(console.error);
    }, []);

    // Effect: Setup resize event handler for sticky functionality
    useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }

        checkStickyState();
        window.addEventListener("resize", checkStickyState);

        return () => {
            window.removeEventListener("resize", checkStickyState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect: Update sticky basket height (equivalent to componentDidUpdate)
    useEffect(() => {
        // Clear max-height for mobile and tablet
        if (!isStickyActive && basketRef.current && containerRef.current) {
            containerRef.current.style.maxHeight = "unset";
        }
        // For sticky functionality, calculate current basket height
        // And apply to max-height of the container in laptop
        if (isStickyActive && basketRef.current && containerRef.current) {
            const childHeight = basketRef.current.clientHeight;
            containerRef.current.style.maxHeight = `${childHeight}px`;
        }
    });

    if (isLoading) {
        return (
            <div className={styles.grid}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className={styles.grid}>
            {config.get("ENABLE_CHECKOUT_CAPTCHA") && <CaptchaLibrary />}
            {renderSteps()}
            {renderBasket()}
            {renderBasketErrorModal()}
            <PaymentChildFrameListener
                onCheckoutError={onCheckoutError}
                hasPaymentMethodError={!!messages.payment_method}
            />
        </div>
    );
};
